import axios from "axios";

import { useSDKConfigStore } from "../stores/sdkConfigStore";
import {
  JWT,
  PasskeyDiscoverableLoginBeginResponse,
  PasskeyDiscoverableLoginFinishOptions,
  PasskeyLoginBeginResponse,
  PasskeyLoginFinishOptions,
  PasskeyRegistrationBeginResponse,
  PasskeyRegistrationFinishOptions,
  WebAuthnCredential,
} from "./types";
import { getApiClient } from "./httpClient";

/* ------------------ Constants ------------------ */
/** EIP-712 domain version for authentication. */
const AUTH_VERSION = "1";

/* ------------------ Helpers ------------------ */

/**
 * Creates an EIP-712 typed data object for login authentication.
 *
 * @param timestamp - The Unix timestamp in seconds when the login request is created.
 * @param tenantID - The tenant ID string.
 * @returns EIP-712 structured typed data for signing.
 */
function createLoginTypedData(timestamp: number, tenantID: string) {
  return {
    types: {
      EIP712Domain: [
        { name: "name", type: "string" },
        { name: "version", type: "string" },
      ],
      Login: [
        { name: "timestamp", type: "uint256" },
        { name: "tenantID", type: "string" },
      ],
    },
    primaryType: "Login",
    domain: {
      name: "BrahmaCards",
      version: AUTH_VERSION,
    },
    message: {
      timestamp,
      tenantID,
    },
  };
}

/**
 * Creates a preconfigured Axios instance for SwypeSDK authentication requests.
 *
 * @param baseUrl - The API base URL.
 * @param apiPath - The API path prefix.
 * @param apiKey - The API key for authentication.
 * @param tenantId - The tenant ID for authentication.
 * @returns A configured Axios instance.
 */
function createAuthClient(
  baseUrl: string,
  apiPath: string,
  apiKey: string,
  tenantId: string
) {
  return axios.create({
    baseURL: `${baseUrl}${apiPath}/cards/auth`,
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key": apiKey,
      "ngrok-skip-browser-warning": "true",
      "X-Partner-ID": tenantId,
    },
  });
}

/**
 * Ensures the SDK is initialized and that specific required fields exist in the store.
 *
 * @param requiredFields - List of store keys that must be present.
 * @throws If the SDK is not initialized or any required field is missing.
 * @returns The current SDK config store state.
 */
function ensureSDKInitialized<
  T extends keyof ReturnType<typeof useSDKConfigStore.getState>
>(requiredFields: T[] = []) {
  const state = useSDKConfigStore.getState();

  if (!state.isInitialized) {
    throw new Error("SwypeSDK not initialized. Call initializeSwypeSDK first.");
  }

  for (const field of requiredFields) {
    if (!state[field]) {
      throw new Error(`Missing required SDK config field: ${field}`);
    }
  }

  return state;
}

/**
 * Sends an authentication-related request to the Swype API and updates the SDK store with the new tokens.
 *
 * @param path - The relative API endpoint path (e.g., `/sig/login`, `/token/refresh`).
 * @param payload - The JSON payload to send in the request.
 * @throws If authentication fails or the response is missing the required fields.
 * @returns The new `AuthState` returned by the API.
 */
async function authRequest(path: string, payload: object): Promise<JWT> {
  const { apiKey, baseUrl, apiPath, tenantId, setJWT } = ensureSDKInitialized([
    "apiKey",
    "baseUrl",
    "apiPath",
    "tenantId",
  ]);

  if (!baseUrl || !apiPath || !apiKey || !tenantId) {
    throw new Error("Missing required SDK config fields.");
  }

  const client = createAuthClient(baseUrl, apiPath, apiKey, tenantId);

  const {
    data: { data: jwt },
  } = await client.post<{ data: JWT }>(path, payload);

  if (
    !jwt.accessToken ||
    !jwt.refreshToken ||
    !jwt.expiresIn ||
    !jwt.tokenType
  ) {
    throw new Error(`Auth request failed for ${path}`);
  }

  const enrichedJwt: JWT = {
    ...jwt,
    createdAt: Date.now(),
  };

  setJWT(enrichedJwt);
  return enrichedJwt;
}

/* ------------------ Public Functions ------------------ */

/**
 * Generates a new authentication signature and timestamp using EIP-712 signing.
 *
 * @param signTypedData - A function that signs typed data and returns a signature.
 * @returns An object containing the generated `signature` and the `timestamp`.
 */
export async function generateAuthSign(
  tenantID: string,
  signTypedData: (typedData: any) => Promise<string>
): Promise<{ signature: string; timestamp: number }> {
  const timestamp = Math.floor(Date.now() / 1000);
  const typedData = createLoginTypedData(timestamp, tenantID);
  const signature = await signTypedData(typedData);
  return { signature, timestamp };
}

/**
 * Initializes authentication with the Swype API using the provided signature and timestamp.
 * This is a standalone function that doesn't require SDK initialization.
 *
 * @param signature - The signed authentication signature
 * @param timestamp - The timestamp used in signing
 * @param config - Configuration object containing necessary auth parameters
 * @throws If authentication fails or the response is missing required fields.
 */
export async function initializeAuth(
  signature: string,
  timestamp: number,
  config: {
    eoaAddress: string;
    tenantId: string;
    apiKey: string;
    baseUrl: string;
    apiPath: string;
  }
): Promise<JWT> {
  const { setJWT } = useSDKConfigStore.getState();
  const { eoaAddress, tenantId, apiKey, baseUrl, apiPath } = config;

  if (!baseUrl || !apiPath || !apiKey || !tenantId || !eoaAddress) {
    throw new Error("Missing required auth configuration fields.");
  }

  const client = createAuthClient(baseUrl, apiPath, apiKey, tenantId);

  const {
    data: { data: jwt },
  } = await client.post<{ data: JWT }>("/sig/login", {
    sig: signature,
    eoa: eoaAddress,
    timestamp,
    version: AUTH_VERSION,
  });

  if (
    !jwt.accessToken ||
    !jwt.refreshToken ||
    !jwt.expiresIn ||
    !jwt.tokenType
  ) {
    throw new Error("Auth request failed for /sig/login");
  }

  const enrichedJwt: JWT = {
    ...jwt,
    createdAt: Date.now(),
  };

  console.log("SwypeSDK auth initialized");
  setJWT(enrichedJwt);
  return enrichedJwt;
}

/**
 * Refreshes the authentication tokens using the stored refresh token.
 * This function attempts to obtain new access and refresh tokens from the authentication server
 * using a provided refresh token. It handles potential errors during the API call
 * and ensures all necessary configuration fields are present.
 *
 * @param refreshToken The refresh token to use for obtaining new tokens.
 * @param config Configuration object containing necessary API details:
 *   - `eoaAddress`: EOA for user address.
 *   - `tenantId`: The tenant ID.
 *   - `apiKey`: The API key for authentication.
 *   - `baseUrl`: The base URL of the authentication service.
 *   - `apiPath`: The API path for authentication endpoints.
 * @returns A Promise that resolves to a JWT object containing the new access token,
 *   refresh token, expiration details, token type, and creation timestamp.
 * @throws {Error} If the SDK is not initialized, the refresh token is missing,
 *   required configuration fields are missing, or the API request fails.
 */
export async function refreshAuth(
  refreshToken: string,
  config: {
    eoaAddress: string;
    tenantId: string;
    apiKey: string;
    baseUrl: string;
    apiPath: string;
  }
): Promise<JWT> {
  const { setJWT } = useSDKConfigStore.getState();
  const { eoaAddress, tenantId, apiKey, baseUrl, apiPath } = config;

  if (!baseUrl || !apiPath || !apiKey || !tenantId || !eoaAddress) {
    throw new Error("Missing required auth configuration fields.");
  }

  const client = createAuthClient(baseUrl, apiPath, apiKey, tenantId);

  if (!refreshToken) {
    throw new Error("No refresh token available.");
  }

  try {
    const {
      data: { data: jwt },
    } = await client.post<{ data: JWT }>("/token/refresh", {
      refreshToken: refreshToken,
    });

    if (
      !jwt.accessToken ||
      !jwt.refreshToken ||
      !jwt.expiresIn ||
      !jwt.tokenType
    ) {
      throw new Error(
        "Auth request failed for /token/refresh: Incomplete JWT received."
      );
    }

    const enrichedJwt: JWT = {
      ...jwt,
      createdAt: Date.now(),
    };

    console.log("SwypeSDK auth initialized");
    setJWT(enrichedJwt);
    return enrichedJwt;
  } catch (error) {
    console.error("Error refreshing authentication tokens:", error);
    throw new Error(
      `Failed to refresh authentication tokens: ${
        error instanceof Error ? error.message : "An unknown error occurred."
      }`
    );
  }
}

/**
 * Ensures the current authentication is still valid.
 * Does not automatically refresh tokens—throws if expired.
 *
 * @throws If the SDK is not initialized, the access token is missing, or the token is expired.
 */
export async function ensureValidAuth(): Promise<void> {
  const { jwt } = ensureSDKInitialized(["jwt"]);
  if (!jwt?.accessToken || !jwt.expiresIn || !jwt.createdAt) {
    throw new Error("Authentication missing or incomplete.");
  }

  const now = Math.floor(Date.now() / 1000);
  const expiry = jwt.expiresIn;

  if (now >= expiry) {
    throw new Error("Authentication expired. Please refresh.");
  }
}

/* ------------------ Passkey Functions ------------------ */

export async function beginPasskeyRegistration(): Promise<PasskeyRegistrationBeginResponse> {
  try {
    const client = getApiClient();

    const {
      data: {
        data: { publicKey: registrationOptions },
      },
    } = await client.get<{
      data: { publicKey: PasskeyRegistrationBeginResponse };
    }>("/cards/auth/passkeys/register/begin");

    if (!registrationOptions) {
      throw new Error("Invalid server response: registration options missing.");
    }

    return registrationOptions;
  } catch (err: any) {
    console.error("❌ Error in beginPasskeyRegistration:", err);
    throw new Error(
      `Passkey registration begin failed: ${err?.message || "Unknown error"}`
    );
  }
}

export async function finishPasskeyRegistration(
  options: PasskeyRegistrationFinishOptions
): Promise<{ success: boolean; message: string }> {
  if (!options.response || !options.credentialName) {
    return {
      success: false,
      message: "Missing required options for passkey registration finish.",
    };
  }

  try {
    const client = getApiClient();

    const response = await client.post<{ data: { status: string } }>(
      "/cards/auth/passkeys/register/finish",
      {
        response: options.response,
        credentialName: options.credentialName,
      }
    );

    const {
      data: { data: result },
    } = response;

    const success = response.status === 200;

    return {
      success,
      message: success
        ? "Passkey registration completed successfully."
        : `Passkey registration failed: ${result?.status || "Unknown error"}`,
    };
  } catch (err: any) {
    console.error("❌ Error in finishPasskeyRegistration:", err);
    return {
      success: false,
      message: `Passkey registration finish failed: ${
        err?.message || "Unknown error"
      }`,
    };
  }
}

export async function beginDiscoverablePasskeyLogin(): Promise<PasskeyDiscoverableLoginBeginResponse> {
  const { apiKey, baseUrl, apiPath, tenantId } = ensureSDKInitialized([
    "apiKey",
    "baseUrl",
    "apiPath",
    "tenantId",
  ]);

  if (!baseUrl || !apiPath || !apiKey || !tenantId) {
    throw new Error("Missing required SDK config fields.");
  }

  try {
    const client = createAuthClient(baseUrl, apiPath, apiKey, tenantId);

    const {
      data: { data: response },
    } = await client.post<{ data: PasskeyDiscoverableLoginBeginResponse }>(
      "/passkeys/login/discoverable/begin",
      {}
    );

    if (!response?.sessionId || !response?.options?.publicKey) {
      throw new Error(
        "Invalid server response: sessionId or publicKey missing."
      );
    }

    return response;
  } catch (err: any) {
    console.error("❌ Error in beginDiscoverablePasskeyLogin:", err);
    throw new Error(
      `Discoverable passkey login begin failed: ${
        err?.message || "Unknown error"
      }`
    );
  }
}

export async function finishDiscoverablePasskeyLogin(
  options: PasskeyDiscoverableLoginFinishOptions
): Promise<JWT> {
  try {
    const jwt = await authRequest("/passkeys/login/discoverable/finish", {
      sessionId: options.sessionId,
      response: options.response,
    });

    if (!jwt?.accessToken) {
      throw new Error("Invalid server response: JWT missing.");
    }

    console.log("✅ SwypeSDK discoverable passkey login completed");
    return jwt;
  } catch (err: any) {
    console.error("❌ Error in finishDiscoverablePasskeyLogin:", err);
    throw new Error(
      `Discoverable passkey login finish failed: ${
        err?.message || "Unknown error"
      }`
    );
  }
}

/**
 * Retrieves all registered passkeys for the authenticated user.
 *
 * @returns A promise that resolves to an array of WebAuthnCredential.
 */
export const getPasskeys = async (): Promise<WebAuthnCredential[]> => {
  try {
    const client = getApiClient();
    const response = await client.get<{ data: WebAuthnCredential[] | null }>(
      "/cards/auth/passkeys"
    );
    return response.data.data || [];
  } catch (error) {
    console.error("[getPasskeys]", error);
    // Safe fallback
    return [];
  }
};

/**
 * Deletes a specific passkey by its ID for the authenticated user.
 *
 * @param id - The UUID of the passkey to delete.
 * @returns A promise that resolves to a success message.
 */
export const deletePasskey = async (
  id: string
): Promise<{ status: string }> => {
  try {
    const client = getApiClient();
    const response = await client.delete<{ data: { status: string } }>(
      `/cards/auth/passkeys/${id}`
    );
    return response.data.data;
  } catch (error) {
    console.error("[deletePasskey]", error);
    throw error; // Re-throw to allow error handling in the calling component
  }
};
